import { PROGRESSIVE_SPEECH_PROTOCOL, PROGRESSIVE_SPEECH_LIMITS as LIMITS,
  PROGRESSIVE_SPEECH_ERROR_CODES, type ProgressiveSpeechErrorCode,
} from "../../supabase/functions/_shared/progressiveSpeechProtocol";
import type { MirrorProgressiveSpeechObservation } from "./mirrorProgressiveSpeechTelemetry";
import type { SpeechDeliveryStage } from "../../supabase/functions/_shared/speechDeliveryTiming";

export interface SpeechClip { seq: number; audio: Blob; cached: boolean | null }
export class ProgressiveSpeechFailure extends Error {
  constructor(readonly code: ProgressiveSpeechErrorCode | "protocol" | "playback" | "audio_error" | "cancelled") {
    super("Progressive speech " + code); this.name = "ProgressiveSpeechFailure";
  }
}
function fail(code: ConstructorParameters<typeof ProgressiveSpeechFailure>[0]): never { throw new ProgressiveSpeechFailure(code); }
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const keys = (value: Record<string, unknown>, names: readonly string[]) =>
  Object.keys(value).length === names.length && names.every(name => Object.prototype.hasOwnProperty.call(value, name));
function decodeAudio(value: unknown): Blob {
  if (typeof value !== "string" || !value.length || value.length > Math.ceil(LIMITS.clipAudioBytes / 3) * 4
    || value.length % 4 !== 0 || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) return fail("invalid_audio");
  let binary: string;
  try { binary = atob(value); } catch { return fail("invalid_audio"); }
  if (!binary.length || binary.length > LIMITS.clipAudioBytes || btoa(binary) !== value) return fail("invalid_audio");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "audio/mpeg" });
}
const encodedTotalLimit = Math.ceil(LIMITS.totalAudioBytes / 3) * 4 + LIMITS.clipCount * 1024;

/** One reader, strict framing and bounded bytes. Pulling stops when the player
 * has its current clip plus one ahead; HTTP/proxy buffers are not an ACK. */
export async function* readProgressiveSpeech(
  response: Response, options: {
    signal: AbortSignal; assertCurrent: () => void;
    accepted: () => void; legacy: () => void; streamDone: () => void;
    deliveryPoint?: (stage: SpeechDeliveryStage) => void;
  },
): AsyncGenerator<SpeechClip> {
  const guard = () => { options.assertCurrent(); if (options.signal.aborted) fail("cancelled"); };
  // Called only after the owner/abort guard; diagnostics cannot affect framing.
  const deliveryPoint = (stage: SpeechDeliveryStage) => {
    try { options.deliveryPoint?.(stage); } catch { /* Optional diagnostics. */ }
  };
  let sse = false, json = false, reconstructed = false, transportStarted = false;
  try {
    guard();
    if (!response.ok) return fail(response.status === 402 ? "quota_rejected" : "stream_failed");
    if (!response.body) return fail("stream_failed");
    sse = /^text\/event-stream(?:;|$)/i.test(response.headers.get("content-type") ?? "");
    json = /^application\/json(?:;|$)/i.test(response.headers.get("content-type") ?? "");
    if (!sse && !json) return fail("protocol");
    if (sse) options.accepted(); else options.legacy();
  } catch (error) {
    void response.body?.cancel().catch(() => {}); throw error;
  }
  const reader = response.body!.getReader(), decoder = new TextDecoder("utf-8", { fatal: true });
  let rejectRead: ((error: Error) => void) | null = null;
  const abortReader = () => {
    rejectRead?.(new ProgressiveSpeechFailure("cancelled"));
    void reader.cancel().catch(() => {});
  };
  options.signal.addEventListener("abort", abortReader, { once: true });
  let buffer = "", transportBytes = 0, audioBytes = 0, count: number | null = null, next = 0, done = false;
  try {
    while (true) {
      guard();
      if (sse) {
        const separator = /\r?\n\r?\n/.exec(buffer);
        if (separator) {
          const frame = buffer.slice(0, separator.index);
          buffer = buffer.slice(separator.index + separator[0].length);
          if (new TextEncoder().encode(frame).length > LIMITS.frameBytes) fail("protocol");
          const data: string[] = [];
          for (const line of frame.split(/\r?\n/)) {
            if (!line || line.startsWith(":")) continue;
            if (!line.startsWith("data:")) fail("protocol");
            data.push(line.slice(5).replace(/^ /, ""));
          }
          if (!data.length) continue;
          if (done) fail("protocol");
          let event: unknown;
          try { event = JSON.parse(data.join("\n")); } catch { fail("protocol"); }
          if (!object(event) || event.protocol !== PROGRESSIVE_SPEECH_PROTOCOL) fail("protocol");
          if (event.type === "start") {
            if (count !== null || !keys(event, ["type", "protocol", "clips"]) || !Number.isInteger(event.clips)
              || Number(event.clips) < 1 || Number(event.clips) > LIMITS.clipCount) fail("protocol");
            count = Number(event.clips); continue;
          }
          if (event.type === "error") {
            if (!keys(event, ["type", "protocol", "code"])
              || !PROGRESSIVE_SPEECH_ERROR_CODES.some(code => code === event.code)) fail("protocol");
            fail(event.code as ProgressiveSpeechErrorCode);
          }
          if (count === null) fail("protocol");
          if (event.type === "done") {
            if (!keys(event, ["type", "protocol", "clips"]) || event.clips !== count || next !== count) fail("protocol");
            done = true; options.streamDone(); continue;
          }
          if (event.type !== "clip" || !keys(event, ["type", "protocol", "seq", "audio_base64", "cached", "credit_balance_units"])
            || event.seq !== next || next >= count || typeof event.cached !== "boolean"
            || !(event.credit_balance_units === null || typeof event.credit_balance_units === "number"
              && Number.isSafeInteger(event.credit_balance_units) && event.credit_balance_units >= 0)) fail("protocol");
          const audio = decodeAudio(event.audio_base64);
          audioBytes += audio.size;
          if (audioBytes > LIMITS.totalAudioBytes) fail("invalid_audio");
          guard();
          if (!reconstructed) { reconstructed = true; deliveryPoint("first_mp3_bytes_reconstructed"); }
          yield { seq: next++, audio, cached: event.cached }; guard();
          continue;
        }
        if (new TextEncoder().encode(buffer).length > LIMITS.frameBytes) fail("protocol");
      } else if (transportBytes > LIMITS.frameBytes) fail("protocol");
      guard();
      let timer: ReturnType<typeof setTimeout> | undefined;
      const result = await Promise.race([
        reader.read(),
        new Promise<never>((_, reject) => { rejectRead = reject; }),
        new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new ProgressiveSpeechFailure("timeout")), LIMITS.requestMs); }),
      ]).finally(() => { rejectRead = null; if (timer !== undefined) clearTimeout(timer); });
      guard();
      if (result.done) { deliveryPoint("transport_eof"); buffer += decoder.decode(); break; }
      if (result.value.byteLength > 0 && !transportStarted) {
        transportStarted = true; deliveryPoint("first_nonempty_transport_chunk");
      }
      transportBytes += result.value.byteLength;
      if (transportBytes > encodedTotalLimit) fail("protocol");
      buffer += decoder.decode(result.value, { stream: true });
    }
    if (sse) {
      if (buffer.trim() || !done || count === null || next !== count) fail("protocol");
    } else {
      let value: unknown;
      try { value = JSON.parse(buffer); } catch { fail("protocol"); }
      if (!object(value)) fail("protocol");
      const audio = decodeAudio(value.audio_base64);
      guard(); deliveryPoint("first_mp3_bytes_reconstructed");
      yield { seq: 0, audio, cached: typeof value.cached === "boolean" ? value.cached : null }; guard();
    }
  } finally {
    // Cleanup is captured-reader-only, including after owner revocation.
    options.signal.removeEventListener("abort", abortReader); rejectRead = null;
    void reader.cancel().catch(() => {});
    try { reader.releaseLock(); } catch { /* A cancelled pending read owns no new run. */ }
  }
}

export interface OrderedSpeechPlayer {
  firstStarted: Promise<void>;
  finished: Promise<void>;
  cancel(reason?: "stop" | "superseded" | "unmount" | "owner_changed"): void;
}
/** Complete MP3 clips on the same unlocked HTMLAudioElement. All callbacks
 * capture source+sequence; duplicate ended cannot advance a later source. */
export function playOrderedSpeech(options: {
  source: AsyncIterable<SpeechClip>; audio: HTMLAudioElement; signal: AbortSignal;
  assertCurrent: () => void; ownsAudio: () => boolean;
  setPlaybackMode: () => Promise<unknown>;
  requireEndedState?: () => boolean;
  /** Trusted local legacy sentence count; wire sources keep the protocol cap. */
  maxClips?: number;
  sourceChanged: (url: string) => void;
  loading: (value: boolean) => void; playing: (value: boolean) => void;
  observation?: (event: MirrorProgressiveSpeechObservation) => void;
  firstReady?: (cached: boolean | null) => void; firstPlaying?: () => void;
  complete: () => void; failed: (error: ProgressiveSpeechFailure) => void;
}): OrderedSpeechPlayer {
  type Prepared = SpeechClip & { url: string; ended: boolean };
  const maxClips = options.maxClips ?? LIMITS.clipCount;
  const queue: Prepared[] = [], urls = new Set<string>(), iterator = options.source[Symbol.asyncIterator]();
  let active: Prepared | null = null, advancing: Prepared | null = null;
  let terminal = false, sourceDone = false, pumping = false;
  let received = 0, started = false, playingObserved = false, resolveCapacity: (() => void) | null = null;
  let firstResolve!: () => void, firstReject!: (error: Error) => void;
  const firstStarted = new Promise<void>((resolve, reject) => { firstResolve = resolve; firstReject = reject; });
  let finishResolve!: () => void;
  const finished = new Promise<void>(resolve => { finishResolve = resolve; });
  const observe = (event: MirrorProgressiveSpeechObservation) => {
    if (terminal) return;
    try { void Promise.resolve(options.observation?.(event)).catch(() => {}); } catch { /* Optional telemetry. */ }
  };
  const current = () => {
    if (terminal || options.signal.aborted) return false;
    try { options.assertCurrent(); return true; } catch { return false; }
  };
  const sourceCurrent = (clip: Prepared, requireCurrent = false) => current() && options.ownsAudio()
    && active === clip && options.audio.src === clip.url
    && (!requireCurrent || options.audio.currentSrc === clip.url);
  const dispose = () => {
    resolveCapacity?.(); resolveCapacity = null;
    if (options.ownsAudio()) {
      options.audio.pause(); options.audio.currentTime = 0;
      options.audio.onplay = options.audio.onplaying = options.audio.onended = options.audio.onerror = null;
    }
    for (const url of urls) URL.revokeObjectURL(url);
    urls.clear(); queue.length = 0; active = advancing = null;
    // No shared refs/resources are touched by this captured iterator cleanup.
    void iterator.return?.().catch(() => {});
    finishResolve();
  };
  const stop = (reason: "stop" | "superseded" | "unmount" | "owner_changed" = "stop") => {
    if (terminal) return;
    if (reason !== "owner_changed" && current()) observe({ stage: "cancelled", code: reason });
    terminal = true;
    if (!started) firstResolve();
    dispose();
  };
  const failed = (error: unknown) => {
    if (terminal) return;
    const safe = error instanceof ProgressiveSpeechFailure ? error : new ProgressiveSpeechFailure("stream_failed");
    if (!current()) { stop("owner_changed"); return; }
    observe({ stage: "error", code: safe.code });
    terminal = true;
    if (!started) firstReject(safe);
    dispose(); options.failed(safe);
  };
  const complete = () => {
    if (!terminal && sourceDone && !active && !queue.length && current()) {
      observe({ stage: "ended" }); terminal = true; if (!started) firstResolve(); dispose(); options.complete();
    }
  };
  const advance = async () => {
    if (advancing || terminal || active) return;
    if (!current()) { stop("owner_changed"); return; }
    const clip = queue.shift();
    if (!clip) { complete(); return; }
    advancing = active = clip;
    resolveCapacity?.(); resolveCapacity = null;
    try {
      options.sourceChanged(clip.url);
      const audio = options.audio;
      audio.src = clip.url; audio.load();
      audio.onplay = () => { if (sourceCurrent(clip)) options.playing(true); };
      audio.onplaying = () => {
        if (!sourceCurrent(clip, true) || clip.ended) return;
        if (!playingObserved) { playingObserved = true; observe({ stage: "playing" }); options.firstPlaying?.(); }
      };
      audio.onended = () => {
        if (!sourceCurrent(clip, true) || clip.ended || options.requireEndedState?.() && !audio.ended) return;
        clip.ended = true; active = null;
        // Revoke only this clip's admission. Its pending native/play promise
        // cannot hold the next complete clip or later clear its admission.
        if (advancing === clip) advancing = null;
        if (urls.delete(clip.url)) URL.revokeObjectURL(clip.url);
        options.playing(false);
        resolveCapacity?.(); resolveCapacity = null;
        // A real ended event can precede settlement of play(). Completion
        // must not wait for that unrelated pending admission promise.
        complete();
        if (!advancing && !terminal) void advance().catch(failed);
      };
      audio.onerror = () => { if (sourceCurrent(clip)) failed(new ProgressiveSpeechFailure("audio_error")); };
      if (!sourceCurrent(clip)) { stop("owner_changed"); return; }
      await options.setPlaybackMode();
      if (!sourceCurrent(clip)) { if (!terminal && !clip.ended) stop("owner_changed"); return; }
      await audio.play();
      if (!sourceCurrent(clip)) { if (!terminal && !clip.ended) stop("owner_changed"); return; }
      options.loading(false);
      if (!started) { started = true; firstResolve(); }
    } catch (error) {
      if (!terminal && !clip.ended && active === clip) {
        failed(error instanceof ProgressiveSpeechFailure ? error : new ProgressiveSpeechFailure("playback"));
      }
    } finally {
      if (advancing === clip) {
        advancing = null;
        if (!terminal && !active) void advance().catch(failed);
      }
    }
  };
  const pump = async () => {
    if (pumping) return;
    pumping = true;
    try {
      while (!terminal) {
        if (!current()) { stop("owner_changed"); return; }
        // At most current+one ahead. Demand resumes only after a clip leaves.
        if ((active ? 1 : 0) + queue.length >= 2) {
          await new Promise<void>(resolve => { resolveCapacity = resolve; });
          if (!current()) { if (!terminal) stop("owner_changed"); return; }
          continue;
        }
        const result = await iterator.next();
        if (!current()) { if (!terminal) stop("owner_changed"); return; }
        if (result.done) { sourceDone = true; complete(); return; }
        if (result.value.seq !== received++ || received > maxClips) fail("protocol");
        if (!result.value.audio.size || result.value.audio.size > LIMITS.clipAudioBytes) fail("invalid_audio");
        const url = URL.createObjectURL(result.value.audio); urls.add(url);
        queue.push({ ...result.value, url, ended: false });
        if (received === 1) {
          observe({ stage: "first_clip_ready", cached: result.value.cached });
          options.firstReady?.(result.value.cached);
        }
        void advance().catch(failed);
      }
    } catch (error) { failed(error); }
  };
  const lifetime = setTimeout(() => failed(new ProgressiveSpeechFailure("timeout")), 600_000);
  const aborted = () => stop("stop");
  options.signal.addEventListener("abort", aborted, { once: true });
  void finished.then(() => { clearTimeout(lifetime); options.signal.removeEventListener("abort", aborted); });
  void pump();
  return { firstStarted, finished, cancel: stop };
}
